import React, { useCallback, useMemo, useState } from 'react';
import { Alert, StyleSheet, Text, View } from 'react-native';
import { launchCamera } from 'react-native-image-picker';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getFaceAttendanceConfig } from '../../config/faceAttendanceConfig';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { identifyEmployeeFace } from '../../services/faceAttendancePunchService';
import { logFaceVerification } from '../../services/faceVerificationService';
import type { FaceIdentifyResult } from '../../types/faceAttendance';
import { FaceFlowError, safeFaceFlowError } from '../../utils/faceAttendanceErrors';
import { deleteTemporaryImage } from '../../utils/temporaryImage';

type Props = NativeStackScreenProps<RootStackParamList, 'FaceIdentify'>;

const DEFAULT_THRESHOLD = 0.75;

export function FaceIdentifyScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [busy, setBusy] = useState(false);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [result, setResult] = useState<FaceIdentifyResult | null>(null);
  const [logId, setLogId] = useState<number | null>(null);

  const themed = useMemo(
    () => ({
      section: { ...typography.sectionTitle, color: palette.textPrimary },
      body: { ...typography.body, color: palette.textSecondary },
      value: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error, marginBottom: spacing.md },
      info: { ...typography.caption, color: palette.info, marginBottom: spacing.md },
    }),
    [palette],
  );

  const runIdentify = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setStatusMessage(null);
    setResult(null);
    setLogId(null);

    const config = getFaceAttendanceConfig();
    let imageUri: string | null = null;
    try {
      const capture = await launchCamera({
        mediaType: 'photo',
        cameraType: 'front',
        saveToPhotos: false,
        includeBase64: false,
        quality: config.captureQuality,
        maxWidth: config.imageMaxWidth,
        maxHeight: config.imageMaxHeight,
        presentationStyle: 'fullScreen',
      });
      if (capture.didCancel) throw new FaceFlowError('CAPTURE_CANCELED');
      if (capture.errorCode || !capture.assets?.[0]?.uri) {
        throw new FaceFlowError('CAPTURE_FAILED');
      }
      const asset = capture.assets[0];
      imageUri = asset.uri!;
      const identified = await identifyEmployeeFace({
        image: {
          uri: imageUri,
          fileName: asset.fileName || `face-identify-${Date.now()}.jpg`,
          mimeType: 'image/jpeg',
          width: asset.width || config.imageMaxWidth,
          height: asset.height || config.imageMaxHeight,
        },
        captureSource: config.captureSource,
      });
      setResult(identified);
      setStatusMessage('Employee identified. You can log a verification record.');
    } catch (caught) {
      const flowError = safeFaceFlowError(caught);
      setError(flowError.message);
    } finally {
      if (imageUri) {
        await deleteTemporaryImage(imageUri).catch(() => undefined);
      }
      setBusy(false);
    }
  }, [busy]);

  const runLogVerification = useCallback(async () => {
    if (!result || logging || logId != null) return;
    setLogging(true);
    setError(null);
    try {
      const confidence =
        result.identification.confidence ??
        result.provider.similarity ??
        0;
      const logged = await logFaceVerification({
        employeeId: result.employee.employeeId,
        registrationId: result.registration.faceRegistrationId,
        verificationType: 'IDENTIFY',
        faceDetected: true,
        livenessVerified: false,
        identityMatched: true,
        confidenceScore: confidence,
        configuredThreshold: DEFAULT_THRESHOLD,
      });
      setLogId(logged.verificationLogId);
      setStatusMessage(
        logged.verificationSuccessful
          ? `Verification logged (#${logged.verificationLogId}).`
          : `Verification logged with failure (#${logged.verificationLogId}).`,
      );
    } catch (caught) {
      const flowError = safeFaceFlowError(caught);
      setError(flowError.message);
      Alert.alert('Verification log failed', flowError.message);
    } finally {
      setLogging(false);
    }
  }, [logId, logging, result]);

  return (
    <PermissionGuard
      permission={PERMISSIONS.FACE_VERIFY}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen scroll>
        <ScreenHeader
          title="Face identify"
          onBack={navigation.goBack}
        />

        <View style={styles.body}>

          {busy ? <AppLoader message="Identifying face" /> : null}
          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
          {statusMessage ? (
            <Text accessibilityRole="alert" style={themed.info}>
              {statusMessage}
            </Text>
          ) : null}

          {result ? (
            <AppCard style={styles.card}>
              <Text style={themed.section}>Match</Text>
              <Text style={themed.value}>
                {result.employee.displayName || 'Employee'} (
                {result.employee.employeeNumber || result.employee.employeeId})
              </Text>
              <Text style={themed.body}>
                Confidence:{' '}
                {result.identification.confidence ??
                  result.provider.similarity ??
                  '—'}
              </Text>
              <Text style={themed.body}>
                Provider:{' '}
                {result.registration.biometricProvider ||
                  result.provider.providerCode ||
                  '—'}
              </Text>
              {logId != null ? (
                <Text style={themed.body}>Verification log ID: {logId}</Text>
              ) : null}
            </AppCard>
          ) : null}

          <View style={styles.actions}>
            <AppButton
              title="Capture & Identify"
              onPress={runIdentify}
              loading={busy}
              disabled={busy || logging}
              accessibilityLabel="Capture and identify face"
            />
            {result && logId == null ? (
              <AppButton
                title="Log Verification"
                variant="secondary"
                onPress={runLogVerification}
                loading={logging}
                disabled={busy || logging}
                accessibilityLabel="Log face verification"
              />
            ) : null}
          </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.sm, marginBottom: spacing.md },
  actions: { gap: spacing.sm, marginTop: spacing.md, marginBottom: spacing.xl },
});
